type ImportMetaEnv = {
  VITE_CONVEX_URL?: string
  VITE_CONVEX_SITE_URL?: string
  VITE_SITE_URL?: string
  VITE_TURNSTILE_SITE_KEY?: string
  VITE_STRIPE_PUBLISHABLE_KEY?: string
  VITE_SENTRY_DSN?: string
  VITE_META_PIXEL_ID?: string
  MODE?: string
  DEV?: boolean
  PROD?: boolean
}

function getViteEnv(): ImportMetaEnv {
  if (typeof import.meta !== 'undefined' && (import.meta as { env?: ImportMetaEnv }).env) {
    return (import.meta as { env: ImportMetaEnv }).env
  }
  return {}
}

const viteEnv = getViteEnv()

const procEnv: Record<string, string | undefined> =
  typeof process !== 'undefined' && process.env ? process.env : {}

function resolveEnv(name: string): string | undefined {
  const fromVite = viteEnv[name as keyof ImportMetaEnv]
  const candidate = typeof fromVite === 'string' ? fromVite : undefined
  return candidate ?? procEnv[name] ?? procEnv[`VITE_${name}`]
}

export function requireEnv(name: string, value?: string | undefined) {
  const resolved = value ?? resolveEnv(name)

  if (!resolved) {
    throw new Error(`${name} is required.`)
  }

  return resolved
}

export function readEnv(name: string): string | undefined {
  return resolveEnv(name)
}

export const PUBLIC_CONVEX_URL = viteEnv.VITE_CONVEX_URL ?? procEnv.VITE_CONVEX_URL ?? procEnv.NEXT_PUBLIC_CONVEX_URL
export const PUBLIC_CONVEX_SITE_URL =
  viteEnv.VITE_CONVEX_SITE_URL ?? procEnv.VITE_CONVEX_SITE_URL ?? procEnv.NEXT_PUBLIC_CONVEX_SITE_URL
export const PUBLIC_TURNSTILE_SITE_KEY =
  viteEnv.VITE_TURNSTILE_SITE_KEY ?? procEnv.VITE_TURNSTILE_SITE_KEY ?? ''

// Platform Stripe publishable key (pk_test_… / pk_live_…). The on-page Payment
// Element loads Stripe.js with this key, scoped to each org's connected account
// via `stripeAccount` (direct charges). Public by design — safe in the browser.
export const PUBLIC_STRIPE_PUBLISHABLE_KEY =
  viteEnv.VITE_STRIPE_PUBLISHABLE_KEY ?? procEnv.VITE_STRIPE_PUBLISHABLE_KEY ?? ''

const PUBLIC_SITE_URL_RAW = viteEnv.VITE_SITE_URL ?? procEnv.VITE_SITE_URL ?? procEnv.SITE_URL ?? ''
export const PUBLIC_SITE_URL = PUBLIC_SITE_URL_RAW.replace(/\/+$/, '')

// Sentry DSN for client-side error/replay reporting. Public by design (safe in
// the browser). When empty, Sentry stays disabled — see src/lib/sentry.ts.
export const PUBLIC_SENTRY_DSN = viteEnv.VITE_SENTRY_DSN ?? procEnv.VITE_SENTRY_DSN ?? ''

/**
 * Origin to bake into copy/paste embed snippets shown in the admin editor.
 * Prefers VITE_SITE_URL so production-deployed admins always get the canonical
 * public URL even if the app is served from a different domain. Falls back to
 * window.location.origin so local dev and previews still work without config.
 */
export function getEmbedScriptOrigin(): string {
  if (PUBLIC_SITE_URL) return PUBLIC_SITE_URL
  if (typeof window !== 'undefined') return window.location.origin
  return ''
}
